import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Building2, 
  MapPin, 
  Users, 
  Euro,
  Award,
  Tag,
  Save,
  Loader2,
  ArrowLeft,
  X,
  Plus
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTORS = [
  "Travaux",
  "Informatique",
  "Services",
  "Fournitures",
  "Transport",
  "Santé",
  "Environnement",
  "Communication",
  "Formation",
  "Conseil"
];

const REGIONS = [
  "Auvergne-Rhône-Alpes",
  "Bourgogne-Franche-Comté",
  "Bretagne",
  "Centre-Val de Loire",
  "Corse",
  "Grand Est",
  "Hauts-de-France",
  "Île-de-France",
  "Normandie",
  "Nouvelle-Aquitaine",
  "Occitanie",
  "Pays de la Loire",
  "Provence-Alpes-Côte d'Azur"
];

const EMPLOYEE_RANGES = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "500+"
];

const REVENUE_RANGES = [
  "< 100 000 €",
  "100 000 € - 500 000 €",
  "500 000 € - 1 M€",
  "1 M€ - 5 M€",
  "5 M€ - 10 M€",
  "> 10 M€"
];

const CERTIFICATIONS = [
  "RGE",
  "ISO 9001",
  "ISO 14001",
  "ISO 27001",
  "Qualibat",
  "Qualiopi",
  "MASE",
  "CEFRI",
  "APSAD",
  "NF",
  "CE"
];

const CompanyProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(true);
  const [newKeyword, setNewKeyword] = useState("");
  
  const [formData, setFormData] = useState({
    company_name: "",
    sector: "",
    sub_sectors: [],
    location: "",
    region: "",
    employees: "",
    annual_revenue: "",
    certifications: [],
    keywords: [],
    description: ""
  });

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData(response.data);
        setIsNewProfile(false);
      }
    } catch (error) {
      // No profile yet, that's fine
      console.debug("No company profile found yet:", error?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      const method = isNewProfile ? "post" : "put";
      
      await axios[method](`${API_URL}/api/company`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isNewProfile ? "Profil créé avec succès !" : "Profil mis à jour !");
      setIsNewProfile(false);
      
      // Redirect to dashboard after creating profile
      if (isNewProfile) {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, newKeyword.trim()]
      }));
      setNewKeyword("");
    }
  };

  const removeKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="mb-4 text-slate-600 hover:text-slate-900"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au dashboard
            </Button>
            <h1 className="font-display text-3xl font-bold text-slate-900">
              {isNewProfile ? "Créer votre profil entreprise" : "Modifier votre profil entreprise"}
            </h1>
            <p className="text-slate-600 mt-2">
              Ces informations permettent à notre IA de trouver les marchés les plus adaptés à votre activité.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <Building2 className="w-5 h-5 text-violet-600" />
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Ma Société SAS"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sector">Secteur d'activité *</Label>
                    <Select
                      value={formData.sector}
                      onValueChange={(value) => setFormData({ ...formData, sector: value })}
                    >
                      <SelectTrigger data-testid="sector-select">
                        <SelectValue placeholder="Sélectionner un secteur" />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTORS.map(sector => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description de l'entreprise</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Décrivez votre entreprise, vos compétences clés et votre expertise..."
                    rows={4}
                    data-testid="company-description-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <MapPin className="w-5 h-5 text-violet-600" />
                  Localisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="location">Ville *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Lyon"
                      required
                      data-testid="location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">Région *</Label>
                    <Select
                      value={formData.region}
                      onValueChange={(value) => setFormData({ ...formData, region: value })}
                    >
                      <SelectTrigger data-testid="region-select">
                        <SelectValue placeholder="Sélectionner une région" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map(region => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Size & Revenue */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <Users className="w-5 h-5 text-violet-600" />
                  Taille et chiffre d'affaires
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="employees">Nombre d'employés</Label>
                    <Select
                      value={formData.employees}
                      onValueChange={(value) => setFormData({ ...formData, employees: value })}
                    >
                      <SelectTrigger data-testid="employees-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYEE_RANGES.map(range => (
                          <SelectItem key={range} value={range}>{range}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="annual_revenue">Chiffre d'affaires annuel</Label>
                    <Select
                      value={formData.annual_revenue}
                      onValueChange={(value) => setFormData({ ...formData, annual_revenue: value })}
                    >
                      <SelectTrigger data-testid="revenue-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {REVENUE_RANGES.map(range => (
                          <SelectItem key={range} value={range}>{range}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Certifications */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <Award className="w-5 h-5 text-violet-600" />
                  Certifications
                </CardTitle>
                <CardDescription>
                  Sélectionnez les certifications que possède votre entreprise
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {CERTIFICATIONS.map(cert => (
                    <Badge
                      key={cert}
                      variant={formData.certifications.includes(cert) ? "default" : "outline"}
                      className={`cursor-pointer transition-all ${
                        formData.certifications.includes(cert) 
                          ? "bg-violet-600 hover:bg-violet-700" 
                          : "hover:bg-violet-50 hover:border-violet-300"
                      }`}
                      onClick={() => toggleCertification(cert)}
                      data-testid={`cert-${cert}`}
                    >
                      {cert}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Keywords */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <Tag className="w-5 h-5 text-violet-600" />
                  Mots-clés
                </CardTitle>
                <CardDescription>
                  Ajoutez des mots-clés décrivant vos compétences et domaines d'expertise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="Ex: rénovation, logiciel, maintenance..."
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                    data-testid="keyword-input"
                  />
                  <Button type="button" onClick={addKeyword} variant="outline" data-testid="add-keyword-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.keywords.map(keyword => (
                      <Badge
                        key={keyword}
                        variant="secondary"
                        className="bg-slate-100 text-slate-700 pr-1"
                      >
                        {keyword}
                        <button
                          type="button"
                          onClick={() => removeKeyword(keyword)}
                          className="ml-1 p-1 hover:bg-slate-200 rounded"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard")}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800 text-white px-8"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isNewProfile ? "Créer le profil" : "Enregistrer les modifications"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyProfilePage;
